import React, { Fragment, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Form } from "react-bootstrap";
import AddSubjectModal from "./AddSubjectModal";
import config from "../../../config";

export default function SubjectList() {
  const subjectRef = useRef();

  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [gradeId, setGradeId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchClasses();
    fetchSubjects();
  }, []);

  /* ---------------------------------------
     Fetch Classes (Normalized)
  --------------------------------------- */
  const fetchClasses = async () => {
    try {
      const res = await axios.get(
        `${config.apiBaseUrl}/grades/classes.php?action=list`
      );

      if (Array.isArray(res.data)) {
        setClasses(res.data);
      } else if (res.data.success) {
        setClasses(res.data.data);
      }
    } catch (error) {
      console.error("Failed to load classes");
    }
  };

  /* ---------------------------------------
     Fetch Subjects
  --------------------------------------- */
  const fetchSubjects = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${config.apiBaseUrl}/academic/subjects-list.php`
      );

      if (res.data.success) {
        setSubjects(res.data.data);
      }
    } catch (error) {
      console.error("Failed to load subjects");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    fetchSubjects();
  };

  /* ---------------------------------------
     Unique Grades for Dropdown
  --------------------------------------- */
  const uniqueGrades = [
    ...new Map(classes.map((c) => [c.grade_id, c])).values(),
  ];

  /* ---------------------------------------
     Filtering (Search + Grade)
  --------------------------------------- */
  const filteredSubjects = subjects.filter((sub) => {
    const matchesGrade =
      gradeId === "" || sub.grade_id == gradeId;

    const matchesSearch =
      sub.subject_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      sub.subject_code.toString().includes(searchTerm);

    return matchesGrade && matchesSearch;
  });

  /* ---------------------------------------
     Pagination
  --------------------------------------- */
  const totalPages = Math.ceil(
    filteredSubjects.length / itemsPerPage
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentSubjects = filteredSubjects.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
        <button
          className="btn btn-sm btn-outline-primary"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>

        <button className="btn btn-sm btn-primary">
          {currentPage}
        </button>

        {currentPage + 1 <= totalPages && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            {currentPage + 1}
          </button>
        )}

        {currentPage + 2 <= totalPages && (
          <span>...</span>
        )}

        <button
          className="btn btn-sm btn-outline-primary"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="row">
        <div className="col-xl-12">

          {/* HEADER */}
          <div className="page-title flex-wrap d-flex justify-content-between align-items-center mb-3">

            {/* Search */}
            <div style={{ width: 300 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search subject..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="d-flex align-items-center gap-2">

              {/* Grade Filter */}
              <Form.Select
                size="sm"
                style={{ width: 200 }}
                value={gradeId}
                onChange={(e) => {
                  setGradeId(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Classes</option>

                {uniqueGrades.map((g) => (
                  <option key={g.grade_id} value={g.grade_id}>
                    Class-{g.grade_name}
                  </option>
                ))}
              </Form.Select>

              {/* Add Subject */}
              <button
                className="btn btn-primary"
                onClick={() => subjectRef.current.openModal()}
              >
                + Subject
              </button>

              {/* Refresh */}
              <button
                className="btn btn-success"
                onClick={refreshData}
                disabled={loading}
              >
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          {/* TABLE */}
          <div className="table-responsive pb-50">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Subject</th>
                  <th>Code</th>
                  <th>Class</th>
                  <th>Academic Year</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>

              <tbody>
                {currentSubjects.length > 0 ? (
                  currentSubjects.map((sub, index) => (
                    <tr key={sub.subject_id}>
                      <td>{startIndex + index + 1}</td>
                      <td>{sub.subject_name}</td>
                      <td>{sub.subject_code}</td>
                      <td>
                        Class-{sub.grade_name} - {sub.section_name}
                      </td>
                      <td>{sub.academic_year}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-info me-2">
                          Edit
                        </button>
                        <button className="btn btn-sm btn-danger">
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No subjects found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {renderPagination()}
          </div>

          {/* Modal */}
          <AddSubjectModal
            ref={subjectRef}
            classes={classes}
            refreshSubjects={fetchSubjects}
          />
        </div>
      </div>
    </Fragment>
  );
}